import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { goTarget } from "@/lib/apiClient";
import { Download, Copy, MessageCircle, Zap } from "lucide-react";
import { toast } from "sonner";

export const QRPanel = ({ activeEvent, phone, slug }) => {
  const wrapRef = useRef(null);
  const target = goTarget(slug);

  const download = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "dynaqr-code.png";
    a.click();
    toast.success("QR code downloaded");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(target);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  return (
    <div data-testid="qr-panel" className="relative rounded-2xl border border-border bg-card p-6 sm:p-8 fade-up">
      <div className="flex items-center gap-2 mb-6">
        <Zap className="h-5 w-5 text-accent" strokeWidth={2} />
        <h2 className="font-display text-2xl font-extrabold tracking-tighter">Live QR Code</h2>
      </div>

      <div ref={wrapRef} className="flex justify-center">
        <div className="rounded-2xl bg-white p-5 shadow-lg glow-green">
          <QRCodeCanvas value={target} size={220} level="H" />
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-secondary/60 p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
          <MessageCircle className="h-3.5 w-3.5" /> Currently sending
        </div>
        {activeEvent ? (
          <div data-testid="qr-active-summary">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="live-dot absolute inline-flex h-full w-full rounded-full bg-accent" />
              </span>
              <span className="font-display font-bold text-lg tracking-tight">{activeEvent.name}</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3 whitespace-pre-wrap">
              {activeEvent.message}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="qr-no-active">
            No active event. Create one and set it Live below.
          </p>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          WhatsApp number: <span className="font-medium text-foreground">{phone || "not set"}</span>
        </p>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Button data-testid="qr-download-btn" onClick={download}
          className="flex-1 rounded-full active:scale-95 transition-transform duration-150">
          <Download className="h-4 w-4 mr-2" /> Download PNG
        </Button>
        <Button data-testid="qr-copy-btn" onClick={copyLink} variant="outline"
          className="flex-1 rounded-full active:scale-95 transition-transform duration-150">
          <Copy className="h-4 w-4 mr-2" /> Copy Link
        </Button>
      </div>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        This QR never changes. It always sends whatever event is Live.
      </p>
    </div>
  );
};
